import { Form, Option, useFormApi, useFormState } from "informed";
import * as React from "react";
import { Link } from "react-router-dom";
import { Device, Site, Subject, WebPortalClient } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { ApiClients, ApiPutClientById } from "../../misc/api/AccountServer/AccountApi";
import { QueryCondition, QueryOrderDirection } from "../../misc/api/apiTypes";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import DocumentTitle from "../../misc/components/DocumentTitle";
import { DeviceForm, DevicesFormModel } from "./DeviceForm";
import { DevicesActionButtons } from "./DevicesActionButtons";
import { Authorization } from "../../misc/components/Authorization";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../../Core/SecurityContext";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";

declare const rootUrl: string;

export const CreateDeviceForm = () => {
    const { study, deviceTypes } = useCurrentStudyConfigurationContext();

    const client = useWebPortalApiClient();
    const [error, setError] = React.useState<string>();
    const [successAlert, setSuccessAlert] = React.useState<boolean>(false);
    const [sites, setSites] = React.useState<Site[]>([]);
    const [subjects, setSubjects] = React.useState<Subject[]>([]);
    const [submitting, setSubmitting] = React.useState<boolean>(false);

    const [clientDevices, setClientDevices] = React.useState([]);

    React.useEffect(() => {
        const getDropDowns = async () => {
            setError(undefined);
            const sitesResult = await client.client.getSites(`id,number`, `studyId eq ${study.id}`, "number");
            setSites(sitesResult.value);

            if (sitesResult.value.length === 0)
                setError("There are no sites available to allocate in this study.");

            const subjectsResult = await client.client.getSubjects(`id,number`, `site/studyId eq ${study.id}`, "number");
            setSubjects(subjectsResult.value);

            try {
                const clients = await ApiClients({
                    Filters: [
                        { Member: "serial", Condition: QueryCondition.NotEqual, Values: [""] }, 
                        { Member: "serial", Condition: QueryCondition.NotEqual, Values: [null] }, 
                        { Member: "portal", Condition: QueryCondition.Equal, Values: ["", null, rootUrl, rootUrl.slice(0, -1) ] },
                    ],
                    Order: {
                        Direction: QueryOrderDirection.Ascending,
                        Member: "serial"
                    }
                });

                const deviceClients = clients.data.filter(c => c.scopes.includes("device"));

                setClientDevices(deviceClients);
            } catch (ex) {
                setError("Unable to connect to Vitalograph Account to query for devices");
            }

            
        }

        getDropDowns();
    }, [study]);

    const formApi = useFormApi<DevicesFormModel>();
    
    const mapFormToModel = (form: DevicesFormModel): Device => {
        return ({
            studyId: study.id,
            deviceType: form.deviceType.id,
            serialNumber: form.serialNumber.serial,
            allocatedSiteId: form.allocatedSite?.id,
            allocatedSubjectId: form.allocatedSubject?.id,
        });
    }

    const onSubmit = async () => {
        setSubmitting(true);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSuccessAlert(false);
            setSubmitting(false);
            return setError("Errors exist on the page");
        }

        try {
            const updatedClientModel = {
                ...state.values.serialNumber,
                portal: rootUrl.replace(/\/$/, "")
            }

            const updatedClientResult = await ApiPutClientById(state.values.serialNumber.id, updatedClientModel);

            const device = await client.client.postDevice(mapFormToModel(state.values));

            if (device) {
                setSuccessAlert(true);
            }
        } catch (ex) {
            setError(ex.message);
        } finally {
            setSubmitting(false);
        }

        setSubmitting(false);
    }

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/deviceadministration`}>Device Administration list</Link>?
            </Alert>
            <Alert show={!!error} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Create Device</h2>
                </div>
                <div className="col-md-6">
                    <DevicesActionButtons submitting={submitting} onSaveClick={onSubmit} automationIdPrefix="ActionsHeader" />
                </div>
            </div>
            <DeviceForm editMode={false} clientDevices={clientDevices} sites={sites} subjects={subjects} deviceTypes={deviceTypes.map(d => ({
                id: d.Id,
                name: d.Name
            }))} />
        </>
    );
}

export const CreateDeviceScreen = () => {
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();

    return (<DocumentTitle title="Create Device | Vitalograph Web Portal">
        <Authorization currentPermissions={currentUserPermissions} studyId={study.id} permissionId={PermissionEnum.ViewDevices}>
            <Form<DevicesFormModel>>
                <CreateDeviceForm />
            </Form>
        </Authorization>
    </DocumentTitle>);
}
